/**
 * VOE Service
 *
 * API client for VOE (Verification of Employment) operations.
 */
import { api } from './api'
import type {
  VOETranscriptRequest,
  VOERecord,
  VOEDashboardMetrics,
  VOEMethodStats,
  VOENextAttempt,
  VOERecordFilters,
  VOECallContext,
  VerificationMethod,
  VOETenstreetState,
} from '../types/voe'

export const voeService = {
  // ─── VOE Records ─────────────────────────────────────────

  async listRecords(filters?: VOERecordFilters): Promise<VOERecord[]> {
    const params = new URLSearchParams()
    if (filters?.status) params.set('status', filters.status)
    if (filters?.client_crm_id) params.set('client_crm_id', filters.client_crm_id)
    if (filters?.include_complete) params.set('include_complete', 'true')
    if (filters?.search) params.set('search', filters.search)
    if (filters?.no_employers_only) params.set('no_employers_only', 'true')

    const url = `/v1/voe/records${params.toString() ? `?${params}` : ''}`
    const response = await api.get(url)
    if (!response.data.success) throw new Error('Failed to fetch VOE records')
    return response.data.data
  },

  async getRecord(recordId: string): Promise<VOERecord> {
    const response = await api.get(`/v1/voe/records/${recordId}`)
    if (!response.data.success) throw new Error('Failed to fetch VOE record')
    return response.data.data
  },

  /** Full SSN + DOB for the record's driver. Server audit-logs every call —
   *  only fetch on an explicit user action (the SsnReveal eye), never eagerly. */
  async revealDriverSsn(recordId: string): Promise<{ ssn: string; dob: string | null }> {
    const response = await api.get(`/v1/voe/records/${recordId}/driver-ssn`)
    if (!response.data.success) throw new Error('Failed to fetch SSN')
    return response.data.data
  },

  // ─── Employers ───────────────────────────────────────────

  async addEmployer(data: {
    voe_record_id: string
    name: string
    phone?: string
    email?: string
    contact_name?: string
    city?: string
    state?: string
    employment_start?: string
    employment_end?: string
    position_held?: string
    employment_history_id?: string
  }) {
    const response = await api.post('/v1/voe/employers', data)
    if (!response.data.success) throw new Error('Failed to add employer')
    return response.data.data
  },

  async updateEmployer(
    employerId: string,
    data: Record<string, unknown>,
  ) {
    const response = await api.patch(`/v1/voe/employers/${employerId}`, data)
    if (!response.data.success) throw new Error('Failed to update employer')
    return response.data.data
  },

  async deleteEmployer(employerId: string) {
    const response = await api.delete(`/v1/voe/employers/${employerId}`)
    if (!response.data.success) throw new Error('Failed to delete employer')
    return response.data.data
  },

  async verifyEmployer(employerId: string, data: {
    verification_method: VerificationMethod
    employment_start?: string
    employment_end?: string
    position_held?: string
    subject_to_fmcsa?: boolean
    has_accidents?: boolean
    accident_details?: string
    has_violations?: boolean
    violation_details?: string
    reason_for_leaving?: string
    eligible_for_rehire?: boolean
    respondent_name?: string
    respondent_title?: string
    subject_to_drug_alcohol_testing?: boolean
    has_drug_alcohol_violation?: boolean
    drug_alcohol_violation_details?: string
    completed_sap_program?: boolean
    was_disqualified?: boolean
    work_record_notes?: string
  }) {
    const response = await api.post(`/v1/voe/employers/${employerId}/verify`, data)
    if (!response.data.success) throw new Error('Failed to verify employer')
    return response.data.data
  },

  async revertToPending(employerId: string) {
    const response = await api.post(`/v1/voe/employers/${employerId}/revert-to-pending`)
    if (!response.data.success) throw new Error('Failed to revert employer')
    return response.data.data
  },

  async markUnableToVerify(
    employerId: string,
    verification_method: VerificationMethod,
    notes?: string,
    force?: boolean,
    unable_reason?: string,
  ) {
    const body: Record<string, unknown> = { verification_method }
    if (notes) body.notes = notes
    if (force) body.force = true
    if (unable_reason) body.unable_reason = unable_reason
    const response = await api.post(`/v1/voe/employers/${employerId}/unable-to-verify`, body)
    if (!response.data.success) throw new Error('Failed to mark unable to verify')
    return response.data.data
  },

  // ─── Contact Attempts ────────────────────────────────────

  async getNextAttempt(employerId: string, method: string): Promise<VOENextAttempt> {
    const response = await api.get(`/v1/voe/employers/${employerId}/next-attempt?method=${method}`)
    if (!response.data.success) throw new Error('Failed to check next attempt')
    return response.data.data
  },

  async logAttempt(employerId: string, data: {
    method: string
    outcome: string
    contact_used?: string
    call_duration_seconds?: number
    notes?: string
    // Twilio Call SID, when the attempt came from the softphone or the desk
    // phone. The backend resolves it to voe_contact_attempts.call_log_id,
    // which gives the nightly transcription drain this call's own three
    // names to bias on instead of a 7-day window of everyone's VOE work.
    call_sid?: string
  }) {
    const response = await api.post(`/v1/voe/employers/${employerId}/attempts`, data)
    if (!response.data.success) throw new Error('Failed to log attempt')
    return response.data.data
  },

  async updateAttempt(attemptId: string, data: {
    outcome?: string
    notes?: string
    contact_used?: string
  }) {
    const response = await api.patch(`/v1/voe/attempts/${attemptId}`, data)
    if (!response.data.success) throw new Error('Failed to update attempt')
    return response.data.data
  },

  async deleteAttempt(attemptId: string) {
    const response = await api.delete(`/v1/voe/attempts/${attemptId}`)
    if (!response.data.success) throw new Error('Failed to delete attempt')
  },

  // ─── Email ───────────────────────────────────────────────

  async previewEmail(employerId: string) {
    const response = await api.get(`/v1/voe/employers/${employerId}/email/preview`)
    if (!response.data.success) throw new Error('Failed to preview email')
    return response.data.data as { to: string; subject: string; body: string }
  },

  async sendEmail(employerId: string, data: {
    to: string
    subject: string
    cc?: string[]
    template_name?: string
    /**
     * Which signed release to attach, when the operator picked one on the send
     * modal instead of taking the default. Server-side it is validated to
     * belong to the same driver; omit it to get the default resolution.
     */
    attachment_driver_file_id?: string
  }) {
    const response = await api.post(`/v1/voe/employers/${employerId}/email`, data)
    if (!response.data.success) throw new Error('Failed to send email')
    return response.data.data
  },

  async bulkSendEmails(voeRecordId?: string): Promise<{
    sent: number
    skipped: number
    failed: number
    details: Array<{
      employer_id: string
      employer_name: string
      status: 'sent' | 'failed'
      sent_to?: string
      attempt_number?: number
      reason?: string
    }>
  }> {
    const body: Record<string, unknown> = {}
    if (voeRecordId) body.voe_record_id = voeRecordId
    const response = await api.post('/v1/voe/employers/bulk-send-emails', body)
    if (!response.data.success) throw new Error('Failed to send bulk emails')
    return response.data.data
  },

  async sendVerificationForm(employerId: string, data?: { email_to?: string }) {
    const response = await api.post(`/v1/voe/employers/${employerId}/send-form`, data || {})
    if (!response.data.success) throw new Error('Failed to send verification form')
    return response.data.data as {
      email_id: string
      attempt_id: string
      attempt_number: number
      sent_to: string
    }
  },

  // ─── Call Context ──────────────────────────────────────────

  async getCallContext(employerId: string): Promise<VOECallContext> {
    const response = await api.get(`/v1/voe/employers/${employerId}/call-context`)
    if (!response.data.success) throw new Error('Failed to fetch call context')
    return response.data.data
  },

  // ─── Dashboard ───────────────────────────────────────────

  async getDashboard(): Promise<VOEDashboardMetrics> {
    const response = await api.get('/v1/voe/dashboard')
    if (!response.data.success) throw new Error('Failed to fetch dashboard')
    return response.data.data
  },

  async getMethodStats(filters?: {
    client_crm_id?: string
    date_from?: string
    date_to?: string
  }): Promise<VOEMethodStats> {
    const params = new URLSearchParams()
    if (filters?.client_crm_id) params.set('client_crm_id', filters.client_crm_id)
    if (filters?.date_from) params.set('date_from', filters.date_from)
    if (filters?.date_to) params.set('date_to', filters.date_to)
    const url = `/v1/voe/dashboard/method-stats${params.toString() ? `?${params}` : ''}`
    const response = await api.get(url)
    if (!response.data.success) throw new Error('Failed to fetch method stats')
    return response.data.data
  },

  // ─── FMCSA Lookup ──────────────────────────────────────────

  async lookupFmcsa(employerId: string): Promise<{
    success: boolean
    candidates?: Array<Record<string, string | null>>
    current?: Record<string, string | null>
    driver_location?: { city: string | null; state: string | null }
    detail?: string
  }> {
    const response = await api.post(`/v1/voe/employers/${employerId}/lookup-fmcsa`)
    return response.data
  },

  async applyFmcsa(employerId: string, fields: Record<string, string>): Promise<{
    success: boolean
    applied: string[]
  }> {
    const response = await api.post(`/v1/voe/employers/${employerId}/apply-fmcsa`, fields)
    return response.data
  },

  // ─── Tenstreet Xchange bot ─────────────────────────────────

  async getTenstreet(employerId: string): Promise<VOETenstreetState & {
    candidates: { provider_id: string; label: string }[]
    confirm_page_text: string | null
    fmcsa_report_draft: string | null
  }> {
    const response = await api.get(`/v1/voe/employers/${employerId}/tenstreet`)
    return response.data
  },

  async pickTenstreetListing(employerId: string, providerId: string): Promise<VOETenstreetState> {
    const response = await api.post(`/v1/voe/employers/${employerId}/tenstreet/pick`, { provider_id: providerId })
    return response.data
  },

  // ─── Driver Follow-up ─────────────────────────────────────────

  async resendFollowup(followupId: string): Promise<{ sent: boolean }> {
    const response = await api.post(`/v1/voe/driver-followup/${followupId}/resend`)
    if (!response.data.success) throw new Error('Failed to resend follow-up')
    return response.data.data
  },

  async getFollowupCandidates(recordId: string): Promise<{
    driver_name: string
    client_name: string | null
    driver_email: string | null
    hiring_contacts: Array<{ id: string; name: string; email: string }>
    existing_followup: {
      id: string
      status: string
      sent_at: string | null
      expires_at: string | null
    } | null
    employers: Array<{
      id: string
      name: string
      city: string | null
      state: string | null
      phone: string | null
      email: string | null
      contact_name: string | null
      position_held: string | null
      employment_start: string | null
      employment_end: string | null
    }>
  }> {
    const response = await api.get(`/v1/voe/driver-followup/records/${recordId}/candidates`)
    if (!response.data.success) throw new Error('Failed to load candidates')
    return response.data.data
  },

  async createManualFollowup(
    recordId: string,
    payload: {
      employer_ids: string[]
      driver_email?: string | null
      cc_emails?: string[]
      bcc_emails?: string[]
    },
  ): Promise<{
    followup_id: string
    status: string
    sent_at: string
    expires_at: string
    employer_count: number
  }> {
    const response = await api.post(`/v1/voe/driver-followup/records/${recordId}`, payload)
    if (!response.data.success) throw new Error('Failed to create follow-up')
    return response.data.data
  },

  // ─── DQF Completion Requests ──────────────────────────────────
  // Drivers stalled at "Hired - Pending Final Screening" with an incomplete
  // Driver Qualification File. The DQF Completion Request emails the driver a
  // token form to collect the missing components.

  async getDqfCandidates(filters?: {
    client_id?: string
    consultant_user_id?: string
  }): Promise<{ candidates: DQFCandidate[]; count: number }> {
    const params = new URLSearchParams()
    if (filters?.client_id) params.set('client_id', filters.client_id)
    if (filters?.consultant_user_id) params.set('consultant_user_id', filters.consultant_user_id)
    const url = `/v1/voe/dqf-completion/candidates${params.toString() ? `?${params}` : ''}`
    const response = await api.get(url)
    if (!response.data.success) throw new Error('Failed to load DQF candidates')
    return response.data.data
  },

  async createDqfRequest(
    employeeId: string,
    payload: {
      driver_email?: string | null
      cc_emails?: string[]
      bcc_emails?: string[]
    },
  ): Promise<{
    request_id: string
    status: string
    mode: string
    missing_components: string[]
    application_id: string | null
    sent_at: string | null
    expires_at: string
  }> {
    const response = await api.post(`/v1/voe/dqf-completion/records/${employeeId}`, payload)
    if (!response.data.success) throw new Error('Failed to create DQF request')
    return response.data.data
  },

  async resendDqfRequest(requestId: string): Promise<{
    request_id: string
    status: string
    expires_at: string
  }> {
    const response = await api.post(`/v1/voe/dqf-completion/${requestId}/resend`)
    if (!response.data.success) throw new Error('Failed to resend DQF request')
    return response.data.data
  },

  async cancelDqfRequest(requestId: string): Promise<{
    request_id: string
    status: string
  }> {
    const response = await api.post(`/v1/voe/dqf-completion/${requestId}/cancel`)
    if (!response.data.success) throw new Error('Failed to cancel DQF request')
    return response.data.data
  },

  async getDqfRequestDetail(requestId: string): Promise<DQFRequestDetail> {
    const response = await api.get(`/v1/voe/dqf-completion/requests/${requestId}`)
    if (!response.data.success) throw new Error('Failed to load DQF request')
    return response.data.data
  },
}

// ─── DQF Completion types ───────────────────────────────────────

/** A DQF component the gate / collection request tracks. */
export type DQFComponent =
  | 'application'
  | 'authorization'
  | 'employment_history'
  | 'cdl_copy'
  | 'med_cert'
  | 'mvr'
  | 'road_test'

/** Existing active DQF request summary, attached to a candidate row. */
export interface DQFActiveRequest {
  id: string
  status: string // sent | reminded | completed | expired | cancelled
  sent_at: string | null
  expires_at: string | null
  view_count: number
}

/** A driver stalled at "Hired - Pending Final Screening" with an incomplete DQF. */
export interface DQFCandidate {
  employee_id: string
  crm_lead_id: string | null
  driver_name: string
  driver_email: string | null
  client_id: string | null
  client_name: string | null
  missing_components: DQFComponent[]
  mode: string // 'full' | 'section_fill'
  phase_entered_at: string | null
  active_request: DQFActiveRequest | null
}

/** Full DQF request detail for the ops drawer. */
export interface DQFRequestDetail {
  id: string
  employee_id: string
  application_id: string | null
  client_crm_id: string | null
  client_name: string | null
  mode: string
  missing_components: DQFComponent[]
  status: string
  driver_email: string | null
  hiring_contact_emails: string[]
  initial_email_sent_at: string | null
  reminder_email_sent_at: string | null
  completed_at: string | null
  expired_at: string | null
  cancelled_at: string | null
  token_expires_at: string | null
  view_count: number
  last_viewed_at: string | null
  employers_collected: number
  voe_record_id: string | null
}


// ─── Transcript-first VOE (Track B) ─────────────────────────────────────
//
// Every call below 404s while TRANSCRIPT_VOE_ENABLED is off. Callers treat a
// 404 as "this surface does not exist", NOT as an error worth showing — the
// panel simply stays hidden.

export const voeTranscriptService = {
  /** Transcript requests for one VOE record, newest first. Returns [] when the
   *  feature is off, so no caller needs to know about the flag. */
  async listForRecord(recordId: string): Promise<VOETranscriptRequest[]> {
    try {
      const response = await api.get(`/v1/voe/transcript/records/${recordId}`)
      if (!response.data?.success) return []
      return response.data.data as VOETranscriptRequest[]
    } catch (err: any) {
      if (err?.response?.status === 404) return []
      throw err
    }
  },

  /** Kathy's one click on a tier-B candidate. Still a CORROBORATION: the
   *  employer's verification_status does not move, and the response echoes it
   *  back so the UI can show that it did not. */
  async confirmTierB(
    requestId: string,
    body: { employer_id: string; payer_name: string; payer_ein?: string | null },
  ): Promise<{ employer_id: string; verification_method: string | null; verification_status: string }> {
    const response = await api.post(`/v1/voe/transcript/requests/${requestId}/confirm-tier-b`, body)
    if (!response.data?.success) throw new Error('Failed to confirm transcript match')
    return response.data.data
  },

  /** Delete-on-dispute: there is no IRS reinvestigation channel and
   *  15 U.S.C. 1681i requires a free one, so the disputed payer is dropped
   *  from the corroboration and that employer is verified the ordinary way. */
  async dispute(
    requestId: string,
    body: { payer_name: string; employer_id?: string; note?: string },
  ): Promise<VOETranscriptRequest> {
    const response = await api.post(`/v1/voe/transcript/requests/${requestId}/dispute`, body)
    if (!response.data?.success) throw new Error('Failed to record dispute')
    return response.data.data as VOETranscriptRequest
  },
}
